import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  adminLoading: boolean;
  isClient: boolean;
  clientId: string | null;
  clientLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUp: (
    email: string,
    password: string,
    metadata?: { full_name?: string; role?: string }
  ) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<{ error: AuthError | null }>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
}

/* Create a context that can be consumed by components */
const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminLoading, setAdminLoading] = useState(true);
  const [isClient, setIsClient] = useState(false);
  const [clientId, setClientId] = useState<string | null>(null);
  const [clientLoading, setClientLoading] = useState(true);

  /** Check if the logged‑in user is an admin */
  const checkAdminStatus = async (email: string | undefined) => {
    if (!email) {
      setIsAdmin(false);
      setAdminLoading(false);
      return;
    }

    setAdminLoading(true);
    try {
      const { data, error } = await supabase
        .from('admin_users')
        .select('id')
        .eq('email', email)
        .maybeSingle();

      if (!error && data) {
        setIsAdmin(true);
      } else {
        setIsAdmin(false);
      }
    } catch (err) {
      console.error('Admin check failed:', err);
      setIsAdmin(false);
    } finally {
      setAdminLoading(false);
    }
  };

  /** Check if the logged‑in user is a client */
  const checkClientStatus = async (email: string | undefined) => {
    if (!email) {
      setIsClient(false);
      setClientId(null);
      setClientLoading(false);
      return;
    }

    setClientLoading(true);
    try {
      const { data, error } = await supabase
        .from('client_users')
        .select('client_id')
        .eq('email', email)
        .maybeSingle();

      if (!error && data) {
        setIsClient(true);
        setClientId(data.client_id);
      } else {
        setIsClient(false);
        setClientId(null);
      }
    } catch (err) {
      console.error('Client check failed:', err);
      setIsClient(false);
      setClientId(null);
    } finally {
      setClientLoading(false);
    }
  };

  /** Check both admin and client status */
  const checkUserRole = async (email: string | undefined) => {
    await Promise.all([
      checkAdminStatus(email),
      checkClientStatus(email)
    ]);
  };

  /** Initialise session and listen for auth changes */
  useEffect(() => {
    // Get the initial session
    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        checkUserRole(session?.user?.email);
      })
      .catch((err) => {
        console.error('Failed to get session:', err);
        setLoading(false);
      });

    // Subscribe to auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      checkUserRole(session?.user?.email);
    });

    // Cleanup subscription on unmount
    return () => subscription.unsubscribe();
  }, []);

  /** Sign‑in with email & password */
  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      return { error };
    } catch (err) {
      console.error('Sign‑in error:', err);
      return { error: err as AuthError };
    }
  };

  /** Sign‑up with optional metadata */
  const signUp = async (
    email: string,
    password: string,
    metadata?: { full_name?: string; role?: string }
  ) => {
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: metadata },
      });
      return { error };
    } catch (err) {
      console.error('Sign‑up error:', err);
      return { error: err as AuthError };
    }
  };

  /** Sign‑out and reset admin and client flags */
  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      setIsAdmin(false);
      setIsClient(false);
      setClientId(null);
      return { error };
    } catch (err) {
      console.error('Sign‑out error:', err);
      return { error: err as AuthError };
    }
  };

  /** Trigger a password‑reset email */
  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      return { error };
    } catch (err) {
      console.error('Reset password error:', err);
      return { error: err as AuthError };
    }
  };

  const value: AuthContextType = {
    user,
    session,
    loading,
    isAdmin,
    adminLoading,
    isClient,
    clientId,
    clientLoading,
    signIn,
    signUp,
    signOut,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Hook to consume the authentication context */
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
